import { sortAsc, sortDateAsc, sortDateDesc, sortDesc } from '@strata/helper-functions';

import { UNASSIGNED_FILTER, UNASSIGNED_LABEL } from 'services/requestHelpers';

/** Stable built-in queue ids from agora-monitor migrations 00766 and 00769. */
export const BUILT_IN_QUEUE_IDS = {
    openRequests: 'a1000000-0000-4000-8000-000000000001',
    unassigned: 'a1000000-0000-4000-8000-000000000002',
    needsResponse: 'a1000000-0000-4000-8000-000000000003',
    assignedToMe: 'a1000000-0000-4000-8000-000000000004',
    needsAction: 'a1000000-0000-4000-8000-000000000005',
};

/** Needs Action stays hidden until the follow-up flag is on. */
export const omitNeedsActionQueue = (queues, showScheduleFollowUp) => {
    const list = queues ?? [];
    if (showScheduleFollowUp) {
        return list;
    }
    return list.filter((queue) => queue.queueId !== BUILT_IN_QUEUE_IDS.needsAction);
};

export const ASSIGNED_TO_ME = 'me';
export const ASSIGNED_TO_ME_LABEL = 'Assigned to me';

export const DEFAULT_QUEUE_SORT = [{ id: 'urgency', desc: true }];

export const CRITERIA_FIELDS = [
    { key: 'state', label: 'Status', rowField: 'state' },
    { key: 'request_type_code', label: 'Request type code', rowField: 'requestTypeCode' },
    { key: 'request_category_key', label: 'Request category key', rowField: 'requestCategoryKey' },
    { key: 'urgency_band', label: 'Urgency', rowField: 'urgencyBand' },
    { key: 'pharmacy_npi', label: 'Pharmacy NPI', rowField: 'pharmacyNpi' },
    { key: 'assigned_to', label: 'Assignee', rowField: 'assignedTo' },
    { key: 'needs_action', label: 'Needs action', rowField: 'needsAction' },
];

const CRITERIA_FIELD = Object.fromEntries(
    CRITERIA_FIELDS.map(({ key, rowField }) => [key, rowField]),
);

const SORT_ACCESSOR = {
    urgency: (row) => row.urgencyScore,
    needs_by_date: (row) => row.needsByDate,
    sla_business_minutes_remaining: (row) => row.sla?.businessMinutesRemaining,
    state: (row) => row.state,
    assigned_to: (row) => row.assignedTo,
};

const WIRE_SORT_TO_GRID_ID = {
    urgency: 'urgency',
    needs_by_date: 'needsByDate',
    sla_business_minutes_remaining: 'slaBusinessMinutesRemaining',
    state: 'state',
    assigned_to: 'assignedTo',
};

export const mapQueueItem = (queue) => ({
    queueId: queue.queue_id,
    name: queue.name,
    visibility: queue.visibility,
    ownerUpn: queue.owner_upn ?? undefined,
    builtIn: queue.built_in,
    criteria: queue.criteria ?? {},
    sort: queue.sort?.length ? queue.sort : DEFAULT_QUEUE_SORT,
});

export const partitionQueuesForRail = (queues) => {
    const list = queues ?? [];
    const assignedToMe = list.find((queue) => queue.queueId === BUILT_IN_QUEUE_IDS.assignedToMe);
    const teamQueues = list.filter(
        (queue) =>
            queue.visibility === 'shared' && queue.queueId !== BUILT_IN_QUEUE_IDS.assignedToMe,
    );
    const myQueues = [
        ...(assignedToMe ? [assignedToMe] : []),
        ...list.filter((queue) => queue.visibility === 'personal'),
    ];
    return { teamQueues, myQueues };
};

export const canMutateQueue = (queue, viewerUpn) =>
    !!queue && !queue.builtIn && !!viewerUpn && queue.ownerUpn === viewerUpn;

export const wireSortToGridSort = (sort) =>
    (sort?.length ? sort : DEFAULT_QUEUE_SORT).map(({ id, desc }) => ({
        id: WIRE_SORT_TO_GRID_ID[id] ?? id,
        // Missing desc matches DEFAULT_QUEUE_SORT (urgency descending).
        desc: desc ?? true,
    }));

const matchesAssignedTo = (rowValue, allowed, viewerUpn) => {
    const expanded = allowed.map((value) => {
        if (value === ASSIGNED_TO_ME) {
            return viewerUpn;
        }
        if (value === UNASSIGNED_FILTER) {
            return null;
        }
        return value;
    });
    const normalized = rowValue ?? null;
    return expanded.includes(normalized);
};

const matchesCriteriaKey = (row, key, values, viewerUpn) => {
    if (!values?.length) {
        return true;
    }
    if (key === 'assigned_to') {
        return matchesAssignedTo(row.assignedTo, values, viewerUpn);
    }
    if (key === 'needs_action') {
        return values.includes(String(row.needsAction === true));
    }
    const field = CRITERIA_FIELD[key];
    if (!field) {
        return true;
    }
    return values.includes(row[field]);
};

const rowMatchesCriteria = (row, criteria, viewerUpn) => {
    const entries = Object.entries(criteria ?? {}).filter(([, values]) => values?.length);
    if (!entries.length) {
        return true;
    }
    return entries.every(([key, values]) => matchesCriteriaKey(row, key, values, viewerUpn));
};

const sortRequests = (rows, sort) => {
    const specs = (sort?.length ? sort : DEFAULT_QUEUE_SORT)
        .map(({ id, desc }) => {
            const accessor = SORT_ACCESSOR[id];
            if (!accessor) {
                return null;
            }
            const descending = desc ?? true;
            if (id === 'needs_by_date') {
                return {
                    read: accessor,
                    compare: descending ? sortDateDesc : sortDateAsc,
                };
            }
            // sortAsc/sortDesc are string helpers; coerce numbers (urgency, sla minutes).
            return {
                read: (row) => {
                    const value = accessor(row);
                    return value == null ? null : String(value);
                },
                compare: descending ? sortDesc : sortAsc,
            };
        })
        .filter(Boolean);
    if (!specs.length) {
        return [...rows];
    }
    return [...rows].sort((a, b) =>
        specs.reduce((result, { read, compare }) => {
            if (result !== 0) {
                return result;
            }
            return compare(read(a), read(b));
        }, 0),
    );
};

/** Apply saved queue criteria + sort to open-request rows (raw UPNs, before display names). */
export const applyQueueToRequests = (rows, { criteria, sort } = {}, { viewerUpn } = {}) => {
    const filtered = (rows ?? []).filter((row) => rowMatchesCriteria(row, criteria, viewerUpn));
    return sortRequests(filtered, sort);
};

const uniqueSorted = (values) =>
    [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));

export const buildQueueCriteriaOptions = (rows) => {
    const list = rows ?? [];
    return {
        state: ['new', 'in_progress', 'waiting_on_response'],
        urgency_band: ['critical', 'high', 'normal', 'low'],
        request_type_code: uniqueSorted(list.map((row) => row.requestTypeCode)),
        request_category_key: uniqueSorted(list.map((row) => row.requestCategoryKey)),
        pharmacy_npi: uniqueSorted(list.map((row) => row.pharmacyNpi)),
        // Wire sentinels stay as values; text is what CheckboxList shows.
        assigned_to: [
            { value: ASSIGNED_TO_ME, text: ASSIGNED_TO_ME_LABEL },
            { value: UNASSIGNED_FILTER, text: UNASSIGNED_LABEL },
            ...uniqueSorted(list.map((row) => row.assignedTo)),
        ],
        needs_action: [
            { value: 'true', text: 'Yes' },
            { value: 'false', text: 'No' },
        ],
    };
};

export const SORT_OPTIONS = [
    { id: 'urgency', label: 'Urgency' },
    { id: 'needs_by_date', label: 'Needs-by date' },
    { id: 'sla_business_minutes_remaining', label: 'SLA time remaining' },
    { id: 'state', label: 'Status' },
    { id: 'assigned_to', label: 'Assignee' },
];

/** Drop empty arrays so the wire body matches the allowlist omit semantics. */
export const compactCriteria = (criteria) =>
    Object.fromEntries(
        Object.entries(criteria ?? {}).filter(
            ([, values]) => Array.isArray(values) && values.length,
        ),
    );
